import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
    getShippingPackages,
    getShippingPackage,
    ShippingPackage
} from '@/shared/helpers/api.ts';
import { http } from '@/shared/httpWrapper.js';
import { isPackageEditable, packageMetadata } from '@/apps/shipping/shippingPackageMetadata';
import * as signalR from '@microsoft/signalr';
import _ from 'lodash';

async function makeHubConnection(packageStateChangeCallback: (packageId: string) => void): Promise<signalR.HubConnection> {
    const connection = new signalR.HubConnectionBuilder()
        .withUrl(window.config!.signalRUrlShipping, {
            accessTokenFactory: async () => await window.authService.acquireToken()
        })
        .configureLogging(signalR.LogLevel.None)
        .build();

    connection.on('packageStateChange', packageStateChangeCallback);

    await connection.start();

    return connection;
}

export const useShippingStore = defineStore('shipping', () => {
    const packages = ref<ShippingPackage[]>([]);
    const selectedPackage = ref<ShippingPackage | null>(null);
    const showExportWizard = ref<boolean>(false);
    const editingPackage = ref<ShippingPackage | null>(null);
    const startingEdit = ref<boolean>(false);

    const showLog = computed(() => _.get(selectedPackage.value, 'log.length', 0) > 0);
    const showChanges = computed(() => _.get(selectedPackage.value, 'changes.length', 0) > 0);
    const showProgress = computed(() =>
        selectedPackage.value
        && [
            'AssemblyInProgress',
            'AnalyzisInProgress',
            'ImportInProcess'
        ].includes(selectedPackage.value.status));

    const isDownloadAvailable = computed(() => {
        if (!selectedPackage.value)
            return false;

        return selectedPackage.value.status === 'ReadyForDownload';
    });

    const showPackageList = computed(() =>
        selectedPackage.value === null
        && !showExportWizard.value);

    async function refreshSelected() {
        const packageId = selectedPackage.value!.packageId;
        const packageRefresh = await getShippingPackage(http, packageId);
        selectedPackage.value = packageRefresh;
    }

    async function setSelectedPackage(p: ShippingPackage) {
        selectedPackage.value = p;
        await refreshSelected();
    }

    async function init() {
        packages.value = await getShippingPackages(http);
    }

    async function refresh() {
        if (selectedPackage.value?.packageId) {
            await refreshSelected();
        } else {
            await init();
        }
    }

    async function clearSelectedPackage() {
        selectedPackage.value = null;
        await refresh();
    }

    async function startExportWizard() {
        editingPackage.value = null;
        showExportWizard.value = true;
        await clearSelectedPackage();
    }

    async function startEditWizard(p: ShippingPackage) {
        // Ignore repeated clicks while an edit is starting, they would load the setup twice
        if (startingEdit.value)
            return;

        startingEdit.value = true;
        try {
            // Fetch fresh state, the list entry may be outdated
            const packageToEdit = await getShippingPackage(http, p.packageId);
            if (!isPackageEditable(packageToEdit)) {
                await refresh();
                throw new Error(`The package can't be edited while its status is "${packageMetadata.status.displayAs(packageToEdit.status)}".`);
            }
            // Set before showing the wizard, otherwise it renders the package type menu while the list refreshes
            editingPackage.value = packageToEdit;
            showExportWizard.value = true;
            await clearSelectedPackage();
        } finally {
            startingEdit.value = false;
        }
    }

    function closeExportWizard() {
        showExportWizard.value = false;
        editingPackage.value = null;
    }

    makeHubConnection(async packageId => {
        if (packageId === selectedPackage.value?.packageId) {
            if (selectedPackage.value.status !== 'ImportDone') {
                // We ignore ImportDone because there might be a long queue of
                // messages produced right before ImportDone state is reached,
                // but no state change is made after ImportDone.
                await refreshSelected();
            }
        } else if (!selectedPackage.value) {
            await refresh();
        }
    });

    return {
        packages,
        selectedPackage,
        isDownloadAvailable,
        showExportWizard,
        editingPackage,
        startingEdit,
        showPackageList,
        showChanges,
        showLog,
        showProgress,
        init,
        clearSelectedPackage,
        refresh,
        setSelectedPackage,
        startExportWizard,
        startEditWizard,
        closeExportWizard
    };
});
